import { DOWN_ARROW } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';
import { toBoolean } from '@ngx-simple/core/coercion';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { OnChangeType, OnTouchedType, ValidatorOnChange } from '@ngx-simple/core/types';
import { Subject, Subscription } from 'rxjs';
import { DateSelectionModelChange, ExtractDateTypeFromSelection, SimDateSelectionModel } from './date-selection-model';
import { createMissingDateImplError } from './datepicker-errors';

/** 可用于从日历中过滤出日期的方法。 */
export type DateFilterFn<D> = (date: D | null) => boolean;

export class SimDatepickerInputEvent<D, S = unknown> {
  /** 与时间选择器关联 `SimDatepickerInput` 的新值 */
  value: D | null;

  constructor(
    /** 对发出事件的`SimDatepickerInput`组件的引用 */
    public target: SimDatepickerInputBase<S, D>,
    /** 引用与日期选择器`SimDatepickerInput`关联的<input />元素。 */
    public targetElement: HTMLElement
  ) {
    this.value = this.target.value;
  }
}

@Directive()
export abstract class SimDatepickerInputBase<S, D = ExtractDateTypeFromSelection<S>>
  implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy, Validator {
  /** 输入的值 */
  @Input()
  get value(): D | null {
    return this._model ? this._getValueFromModel(this._model.selection) : this._pendingValue;
  }
  set value(value: D | null) {
    value = this._dateAdapter.deserialize(value);
    this._lastValueValid = this._isValidValue(value);
    value = this._dateAdapter.getValidDateOrNull(value);
    const oldDate = this.value;
    this._assignValue(value);
    this._formatValue(value);

    if (!this._dateAdapter.sameDate(oldDate, value)) {
      this._valueChange.emit(value);
    }
  }
  protected _model: SimDateSelectionModel<S, D> | undefined;

  /** datepicker-input是否被禁用 */
  @Input()
  get disabled(): boolean {
    return !!this._disabled || this._parentDisabled();
  }
  set disabled(value: boolean) {
    const newValue = toBoolean(value);
    const element = this._elementRef.nativeElement;

    if (this._disabled !== newValue) {
      this._disabled = newValue;
      this.stateChanges.next(undefined);
    }

    // 我们需要对`blur`方法进行空检查，因为在SSR中它是未定义的。
    // 在Ivy中，在元素附加到DOM之前，会更早地调用静态绑定。
    // 这可能会导致某些浏览器（IE / Edge）抛出错误，这些浏览器断言元素已插入。
    if (newValue && this._isInitialized && element.blur) {
      // 通常，如果本机输入元素被禁用，它们会自动模糊。
      // 这种行为是有问题的，因为这意味着它会触发另一个变更检测周期，
      // 如果输入元素之前位于焦点上，则它将导致检查后错误发生更改。
      element.blur();
    }
  }
  private _disabled: boolean;

  abstract format: string;
  abstract picker: string;

  /** 在此<input>上触发`change`事件时发出`. */
  @Output() readonly dateChange: EventEmitter<SimDatepickerInputEvent<D, S>> = new EventEmitter<SimDatepickerInputEvent<D, S>>();

  /** 在此<input>上触发`input`事件时发出 */
  @Output() readonly dateInput: EventEmitter<SimDatepickerInputEvent<D, S>> = new EventEmitter<SimDatepickerInputEvent<D, S>>();

  // Implemented as part of ControlValueAccessor.
  _controlValueAccessorChangeFn: OnChangeType = () => {};
  // Implemented as part of ControlValueAccessor.
  _onTouched: OnTouchedType = () => {};
  // Implemented as part of Validator.
  _validatorOnChange: ValidatorOnChange = () => {};
  // Implemented as part of Validator.
  protected abstract _validator: ValidatorFn | null;

  /** 当内部状态发生变化时发出 */
  stateChanges = new Subject<void>();

  /** 值更改时（由于用户输入或程序更改）而发出 */
  _valueChange = new EventEmitter<D | null>();

  private _valueChangesSubscription = Subscription.EMPTY;

  /** 组件是否已初始化 */
  private _isInitialized: boolean;

  /** 输入上设置的最后一个值是否有效。 */
  protected _lastValueValid = false;

  /** 用于确定输入是否解析的表单控件验证器 */
  private _parseValidator: ValidatorFn = (): ValidationErrors | null => {
    return this._lastValueValid ? null : { simDatepickerParse: { text: this._elementRef.nativeElement.value } };
  };

  /** 日期过滤器的表单控件验证器 */
  private _filterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(control.value));
    const dateFilter = this._getDateFilter();
    return !dateFilter || !controlValue || dateFilter(controlValue) ? null : { simDatepickerFilter: true };
  };

  /** 最小日期的表单控件验证器 */
  private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(control.value));
    const min = this._getMinDate();
    return !min || !controlValue || this._dateAdapter.compareDate(min, controlValue) <= 0
      ? null
      : { simDatepickerMin: { min: min, actual: controlValue } };
  };

  /** 最大日期的表单控件验证器 */
  private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(control.value));
    const max = this._getMaxDate();
    return !max || !controlValue || this._dateAdapter.compareDate(max, controlValue) >= 0
      ? null
      : { simDatepickerMax: { max: max, actual: controlValue } };
  };

  /**
   * 当选择模型发生变化时，将从不是当前datepicker输入的地方调用的回调函数
   */
  protected abstract _outsideValueChanged?: () => void;

  /**
   * 由于该值保留在输入中分配的模型上，在建立模型之前，我们可能会得到一个值。
   * 这个属性会一直跟踪这个值，直到我们有地方给它赋值。
   */
  private _pendingValue: D | null;

  constructor(protected _elementRef: ElementRef<HTMLInputElement>, @Optional() public _dateAdapter: SimDateAdapter<D>) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('SimDateAdapter');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (dateInputsHaveChanged(changes, this._dateAdapter)) {
      this.stateChanges.next(undefined);
    }
  }

  ngAfterViewInit() {
    this._isInitialized = true;
  }

  ngOnDestroy() {
    this._valueChangesSubscription.unsubscribe();
    this._valueChange.complete();
    this.stateChanges.complete();
  }

  // Implemented as part of Validator.
  registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn;
  }

  // Implemented as part of Validator.
  validate(c: AbstractControl): ValidationErrors | null {
    return this._validator ? this._validator(c) : null;
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: D): void {
    this.value = value;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @HostListener('keydown', ['$event'])
  _onKeydown(event: KeyboardEvent) {
    // tslint:disable-next-line: deprecation
    const isAltDownArrow = event.altKey && event.keyCode === DOWN_ARROW;

    if (isAltDownArrow && !this._elementRef.nativeElement.readOnly) {
      this._openPopup();
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event.target.value'])
  _onInput(value: string) {
    const lastValueWasValid = this._lastValueValid;
    let date = this._dateAdapter.parse(value);
    this._lastValueValid = this._isValidValue(date);
    date = this._dateAdapter.getValidDateOrNull(date);
    if (!this._dateAdapter.sameDate(date, this.value)) {
      this._assignValue(date);
      this._controlValueAccessorChangeFn(date);
      this._valueChange.emit(date);
      this.dateInput.emit(new SimDatepickerInputEvent(this, this._elementRef.nativeElement));
    } else {
      // 调用CVA更改处理程序以获取无效值, 因为这是将控件标记为脏的。
      if (value && !this.value) {
        this._controlValueAccessorChangeFn(date);
      }

      if (lastValueWasValid !== this._lastValueValid) {
        this._validatorOnChange();
      }
    }
  }

  @HostListener('change')
  _onChange() {
    this.dateChange.emit(new SimDatepickerInputEvent(this, this._elementRef.nativeElement));
  }

  @HostListener('click', ['$event'])
  _onClick(event: Event) {
    this._openPopup();
    event.stopPropagation();
  }

  /** 处理输入上的blur事件 */
  @HostListener('blur')
  _onBlur() {
    // 只有当我们有一个有效值时，才重新格式化输入
    if (this.value) {
      this._formatValue(this.value);
    }

    this._onTouched();
  }

  /** 使用输入注册一个日期选择模型 */
  _registerModel(model: SimDateSelectionModel<S, D>): void {
    this._model = model;
    this._valueChangesSubscription.unsubscribe();

    if (this._pendingValue) {
      this._assignValue(this._pendingValue);
    }

    this._valueChangesSubscription = this._model.selectionChanged.subscribe(event => {
      if (event.source !== this) {
        const value = this._getValueFromModel(event.selection);
        this._lastValueValid = this._isValidValue(value);
        this._controlValueAccessorChangeFn(value);
        this._onTouched();
        this._formatValue(value);

        // 我们不能用这种逻辑包装整个块，因为对于范围输入，我们希望在任何一个输入发生更改时重新验证，而目前我们没有很好的方法来区分它。
        if (this._canEmitChangeEvent(event)) {
          this.dateInput.emit(new SimDatepickerInputEvent(this, this._elementRef.nativeElement));
          this.dateChange.emit(new SimDatepickerInputEvent(this, this._elementRef.nativeElement));
        }

        if (this._outsideValueChanged) {
          this._outsideValueChanged();
        }
      }
    });
  }

  /** 格式化值并将其设置在输入元素上 */
  protected _formatValue(value: D | null) {
    this._initFormat();
    this._elementRef.nativeElement.value = value ? this._dateAdapter.format(value, this.format) : '';
  }

  /** 根据picker初始化format */
  private _initFormat() {
    if (!this.format) {
      switch (this.picker) {
        case 'year':
          this.format = 'yyyy';
          break;
        case 'month':
          this.format = 'yyyy-MM';
          break;
        case 'week':
          this.format = 'yyyy-q';
          break;
        case 'date':
          this.format = 'yyyy-MM-dd';
          break;
        case 'datetime':
          this.format = 'yyyy-MM-dd HH:mm:ss';
          break;
        default:
          break;
      }
    }
  }

  /** 为模型分配一个值 */
  private _assignValue(value: D | null) {
    //在模型建立之前，我们可能会得到一些输入值已分配。 保存该值，以便我们以后可以分配它。
    if (this._model) {
      this._assignValueToModel(value);
      this._pendingValue = null;
    } else {
      this._pendingValue = value;
    }
  }

  /** 值是否被视为有效 */
  private _isValidValue(value: D | null): boolean {
    return !value || this._dateAdapter.isValid(value);
  }

  /** 打开与时间选择器关联 `SimDatepickerInput` 的时间选择面板 */
  protected abstract _openPopup(): void;

  /** 谓词，用于确定是否允许我们发出特定的更改事件。 */
  protected abstract _canEmitChangeEvent(event: DateSelectionModelChange<S>): boolean;

  /** 为输入的模型分配一个值 */
  protected abstract _assignValueToModel(model: D | null): void;

  /** 将模型中的值转换为<input value> */
  protected abstract _getValueFromModel(modelValue: S): D | null;

  /** 获取输入的最小日期 用于验证 */
  abstract _getMinDate(): D | null;

  /** 获取输入的最大日期 用于验证 */
  abstract _getMaxDate(): D | null;

  /** 获取日期过滤器方法 用于验证 */
  protected abstract _getDateFilter(): DateFilterFn<D> | undefined;

  /** 获取基本验证器器 */
  protected _getValidators(): ValidatorFn[] {
    return [this._parseValidator, this._minValidator, this._maxValidator, this._filterValidator];
  }

  /**
   * 检查父控件是否已禁用。这样做是为了让它可以被扩展这个的输入覆盖，这个输入可以被放置在一个可以禁用的组中。
   */
  protected _parentDisabled() {
    return false;
  }
}

/**
 * 检查'ngOnChanges'回调中的'SimpleChanges'对象是否有任何更改，考虑到日期对象。
 */
export function dateInputsHaveChanged(changes: SimpleChanges, adapter: SimDateAdapter<unknown>): boolean {
  const keys = Object.keys(changes);

  for (const key of keys) {
    const { previousValue, currentValue } = changes[key];

    if (adapter.isDateInstance(previousValue) && adapter.isDateInstance(currentValue)) {
      if (!adapter.sameDate(previousValue, currentValue)) {
        return true;
      }
    } else {
      return true;
    }
  }

  return false;
}
